import { useState } from 'react';
import { Button } from '@paulownia/shared/components/ui/button';
import { Badge } from '@paulownia/shared/components/ui/badge';
import {
  Clock,
  User,
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  XCircle,
  StickyNote,
  Euro,
  AlertTriangle,
  Phone
} from 'lucide-react';
import { Order, Product, calculateOrderTotal } from '@paulownia/shared/types';
import { validateOrder, deleteOrder } from '@paulownia/shared/services/orders';
import { reportError } from '@paulownia/shared/lib/error-reporting';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@paulownia/shared/components/ui/alert-dialog';
import { OrderLineCard } from './OrderLineCard';

interface ValidationQueueProps {
  orders: Order[];
  products: Product[];
  onComplete: () => void;
}

export function ValidationQueue({ orders, products, onComplete }: ValidationQueueProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isValidating, setIsValidating] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  // Filter to only show awaiting_validation orders
  const pendingOrders = orders.filter(o => o.status === 'awaiting_validation');

  // If no more orders to validate, call onComplete
  if (pendingOrders.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <CheckCircle2 className="h-16 w-16 text-green-500 mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Toutes les commandes ont été traitées
        </h3>
        <p className="text-gray-600 mb-4">
          Il n'y a plus de commandes en attente de validation.
        </p>
        <Button onClick={onComplete}>Fermer</Button>
      </div>
    );
  }

  // Ensure currentIndex is within bounds
  const safeIndex = Math.min(currentIndex, pendingOrders.length - 1);
  const currentOrder = pendingOrders[safeIndex];

  if (!currentOrder) {
    return null;
  }

  const total = calculateOrderTotal(currentOrder.lines, products);
  const pickupDate = new Date(currentOrder.pickupAtISO);

  const goToPrevious = () => {
    if (safeIndex > 0) {
      setCurrentIndex(safeIndex - 1);
    }
  };

  const goToNext = () => {
    if (safeIndex < pendingOrders.length - 1) {
      setCurrentIndex(safeIndex + 1);
    }
  };

  const handleValidate = async () => {
    setIsValidating(true);
    try {
      await validateOrder(currentOrder.id);
      // Stay at same index (next order will slide in) or go to previous if at end
      if (safeIndex >= pendingOrders.length - 1 && safeIndex > 0) {
        setCurrentIndex(safeIndex - 1);
      }
    } catch (error) {
      reportError(error, { context: 'ValidationQueue.handleValidate', orderId: currentOrder.id });
      toast.error('Erreur lors de la validation de la commande');
    } finally {
      setIsValidating(false);
    }
  };

  const handleCancel = async () => {
    setIsCancelling(true);
    try {
      await deleteOrder(currentOrder.id);
      setShowCancelConfirm(false);
      // Stay at same index or go to previous if at end
      if (safeIndex >= pendingOrders.length - 1 && safeIndex > 0) {
        setCurrentIndex(safeIndex - 1);
      }
    } catch (error) {
      reportError(error, { context: 'ValidationQueue.handleCancel', orderId: currentOrder.id });
      toast.error('Erreur lors du refus de la commande');
    } finally {
      setIsCancelling(false);
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Progress indicator */}
      <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b">
        <div className="text-sm text-gray-600">
          Commande <span className="font-semibold">{safeIndex + 1}</span> sur{' '}
          <span className="font-semibold">{pendingOrders.length}</span>
        </div>
        <div className="flex items-center gap-1">
          {pendingOrders.map((_, idx) => (
            <div
              key={idx}
              className={`w-2 h-2 rounded-full transition-colors ${
                idx === safeIndex ? 'bg-amber-500' : 'bg-gray-300'
              }`}
            />
          ))}
        </div>
      </div>

      {/* Order details */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Customer info */}
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
              <User className="h-6 w-6 text-amber-700" />
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-900">
                {currentOrder.customerName}
              </h3>
              <div className="flex items-center gap-1 text-gray-600">
                <Clock className="h-4 w-4" />
                <span>
                  Retrait à{' '}
                  <strong>
                    {pickupDate.toLocaleTimeString('fr-FR', {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </strong>
                </span>
              </div>
            </div>
          </div>
          <Badge className="bg-amber-500">Nouvelle</Badge>
        </div>

        {/* Phone number */}
        {currentOrder.phoneNumber && (
          <div className="flex items-center gap-2 text-gray-600">
            <Phone className="h-4 w-4" />
            <span>{currentOrder.phoneNumber}</span>
          </div>
        )}

        {/* Note */}
        {currentOrder.note && (
          <div className="rounded-lg bg-amber-50 border border-amber-200 p-3">
            <div className="text-xs font-medium text-amber-700 mb-1 flex items-center gap-1">
              <StickyNote className="h-3 w-3" />
              Note du client
            </div>
            <p className="text-sm text-amber-900 whitespace-pre-wrap">{currentOrder.note}</p>
          </div>
        )}

        {/* Products list */}
        <div className="space-y-2">
          <div className="font-medium text-gray-700">Produits commandés</div>
          {currentOrder.lines.map((line, idx) => {
            const product = products.find(p => p.id === line.productId);
            return (
              <OrderLineCard
                key={idx}
                line={line}
                product={product}
                allProducts={products}
              />
            );
          })}
        </div>

        {/* Total */}
        {total !== null && (
          <div className="rounded-lg bg-green-50 border border-green-200 p-4 flex items-center justify-between">
            <span className="text-green-900 font-medium flex items-center gap-2">
              <Euro className="h-5 w-5" />
              Total TTC
            </span>
            <span className="text-2xl font-bold text-green-700">{total.toFixed(2)} €</span>
          </div>
        )}
      </div>

      {/* Navigation and actions */}
      <div className="border-t bg-white p-4 space-y-3">
        {/* Navigation buttons */}
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            size="sm"
            onClick={goToPrevious}
            disabled={safeIndex === 0}
            className="gap-1"
          >
            <ChevronLeft className="h-4 w-4" />
            Précédent
          </Button>
          <span className="text-sm text-gray-500">
            {safeIndex + 1} / {pendingOrders.length}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={goToNext}
            disabled={safeIndex === pendingOrders.length - 1}
            className="gap-1"
          >
            Suivant
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        {/* Action buttons */}
        <div className="grid grid-cols-2 gap-3">
          <Button
            variant="destructive"
            onClick={() => setShowCancelConfirm(true)}
            disabled={isValidating || isCancelling}
            className="gap-2"
          >
            <XCircle className="h-5 w-5" />
            Annuler
          </Button>
          <Button
            onClick={handleValidate}
            disabled={isValidating || isCancelling}
            className="bg-green-600 hover:bg-green-700 gap-2"
          >
            <CheckCircle2 className="h-5 w-5" />
            {isValidating ? 'Validation...' : 'Valider'}
          </Button>
        </div>
      </div>

      {/* Cancel confirmation dialog */}
      <AlertDialog open={showCancelConfirm} onOpenChange={setShowCancelConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              Annuler cette commande ?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Vous êtes sur le point d'annuler la commande de{' '}
              <strong>{currentOrder.customerName}</strong>. Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isCancelling}>Retour</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancel}
              disabled={isCancelling}
              className="bg-red-600 hover:bg-red-700"
            >
              {isCancelling ? 'Annulation...' : 'Confirmer l\'annulation'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
